import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NOTIFICATION_GROUP_LABELS } from '../../../assets/constants/notificationsData';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  LeftArrowIcon,
  NotificationIcon,
} from '../../../assets/icons/icons';
import {
  getUserNotificationsCount,
  formatNotificationTime,
  groupNotifications,
  markAllNotificationsAsRead,
  notificationsApi,
  updateUserNotificationsCount,
} from '../../../assets/services/notifications';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import NotificationDetailSheet from './NotificationDetailSheet';
import { getNotificationPresentation } from './notificationPresentation';
import { useNotificationReadBatcher } from './useNotificationReadBatcher';
import './NotifInbox.css';

function getErrorMessage(error, fallback) {
  return error?.detail
    || error?.payload?.detail
    || error?.message
    || fallback;
}

function getNotificationPreview(notification) {
  if (!notification.loginMeta) return notification.body;

  return [
    notification.loginMeta.city,
    notification.loginMeta.countryTitle,
  ].filter(Boolean).join(', ')
    || notification.loginMeta.userAgent
    || notification.body;
}

function NotificationItem({
  language,
  notification,
  onSelect,
  setCardRef,
}) {
  const { Icon, variant } = getNotificationPresentation(notification.type);
  const notificationTime = formatNotificationTime(notification.createdAt, language);

  return (
    <button
      ref={(node) => setCardRef(notification.id, node)}
      type="button"
      className={
        notification.isRead
          ? 'notif-inbox__item'
          : 'notif-inbox__item notif-inbox__item--unread'
      }
      data-notification-id={notification.id}
      aria-haspopup="dialog"
      onClick={() => onSelect(notification)}
    >
      <div className={`notif-inbox__icon notif-inbox__icon--${variant}`}>
        <Icon className="notif-inbox__item-icon" />
        {!notification.isRead && <span className="notif-inbox__dot" aria-hidden="true" />}
      </div>
      <div className="notif-inbox__copy">
        <span className="notif-inbox__headline">
          <span className="notif-inbox__title">{notification.title}</span>
          <span className="notif-inbox__time">{notificationTime}</span>
        </span>
        <span className="notif-inbox__preview">{getNotificationPreview(notification)}</span>
      </div>
    </button>
  );
}

function NotifInbox({ notificationService = notificationsApi }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { language, notifications: notificationsCopy } = useContext(TranslationContext);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMore, setIsMore] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const cardRefs = useRef(new Map());
  const contentRef = useRef(null);
  const mountedRef = useRef(true);
  const notificationsRef = useRef(notifications);
  const paginationRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const loadingPageLastIdRef = useRef(null);
  const backPath = location.state?.backPath || (location.state?.fromProfile ? '/profile' : '/home');
  const requestIdRef = useRef(0);
  const initialLoadIdRef = useRef(0);
  const unreadLocalCount = notifications.filter((notification) => !notification.isRead).length;
  const unreadCount = getUserNotificationsCount(currentUser, unreadLocalCount);
  const hasUnread = unreadCount > 0;
  const groups = useMemo(() => groupNotifications(notifications), [notifications]);
  const selectedNotification = useMemo(
    () => notifications.find((notification) => notification.id === selectedNotificationId) || null,
    [notifications, selectedNotificationId],
  );

  const {
    discardPendingReads,
    markNotificationViewed,
    observedReadIdsRef,
  } = useNotificationReadBatcher({
    notifications,
    notificationService,
    setCurrentUser,
    setNotifications,
    unreadCount,
  });

  const updateUnreadCount = useCallback((nextCountOrUpdater) => {
    setCurrentUser((prevUser) => updateUserNotificationsCount(
      prevUser,
      nextCountOrUpdater,
      unreadCount
    ));
  }, [setCurrentUser, unreadCount]);

  const setCardRef = useCallback((notificationId, node) => {
    if (node) {
      cardRefs.current.set(notificationId, node);
      return;
    }

    cardRefs.current.delete(notificationId);
  }, []);

  const loadNotificationsPage = useCallback(({ append = false, pageLastId = null } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    return notificationService.getNotificationsPage({ lastId: pageLastId })
      .then((response) => {
        if (!mountedRef.current || requestIdRef.current !== requestId) {
          return;
        }

        const currentNotifications = notificationsRef.current;
        const currentIds = new Set(currentNotifications.map((notification) => notification.id));
        const nextNotifications = append
          ? response.notifications.filter(
            (notification) => notification.id && !currentIds.has(notification.id)
          )
          : response.notifications;
        const nextList = append
          ? [...currentNotifications, ...nextNotifications]
          : nextNotifications;
        const didAdvance = !append || Boolean(response.lastId && response.lastId !== pageLastId);

        notificationsRef.current = nextList;
        setNotifications(nextList);
        setIsMore(Boolean(
          response.isMore
            && response.lastId
            && didAdvance
            && (!append || nextNotifications.length > 0)
        ));
        setLastId(response.lastId);
      });
  }, [notificationService]);

  const loadMoreNotifications = useCallback(async () => {
    if (
      isLoading
        || isLoadingMore
        || isLoadingMoreRef.current
        || !isMore
        || !lastId
        || loadingPageLastIdRef.current === lastId
    ) {
      return;
    }

    isLoadingMoreRef.current = true;
    loadingPageLastIdRef.current = lastId;
    setIsLoadingMore(true);

    try {
      await loadNotificationsPage({ append: true, pageLastId: lastId });
    } catch (error) {
      if (mountedRef.current) {
        setIsMore(false);
        toast.error(getErrorMessage(error, notificationsCopy.loadMoreFailed));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingMore(false);
      }
      isLoadingMoreRef.current = false;
      loadingPageLastIdRef.current = null;
    }
  }, [
    isLoading,
    isLoadingMore,
    isMore,
    lastId,
    loadNotificationsPage,
    notificationsCopy.loadMoreFailed,
  ]);

  const handleMarkAllRead = async () => {
    if (isLoading || isMarkingAllRead || !hasUnread) {
      return;
    }

    setIsMarkingAllRead(true);

    try {
      await notificationService.markAllAsSeen();
      discardPendingReads();
      notificationsRef.current.forEach((notification) => {
        observedReadIdsRef.current.add(notification.id);
      });
      const nextNotifications = markAllNotificationsAsRead(notificationsRef.current);

      notificationsRef.current = nextNotifications;
      setNotifications(nextNotifications);
      updateUnreadCount(0);
    } catch (error) {
      toast.error(getErrorMessage(error, notificationsCopy.markReadFailed));
    } finally {
      if (mountedRef.current) {
        setIsMarkingAllRead(false);
      }
    }
  };

  const handleSelect = (notification) => {
    markNotificationViewed(notification.id);
    setSelectedNotificationId(notification.id);
  };
  const handleBack = () => {
    triggerImpactHaptic('light');
    navigate(backPath);
  };

  useEffect(() => {
    const initialLoadId = initialLoadIdRef.current + 1;
    initialLoadIdRef.current = initialLoadId;
    let isCurrentLoad = true;

    mountedRef.current = true;
    setIsLoading(true);
    setIsMore(false);
    setLastId(null);

    loadNotificationsPage()
      .catch((error) => {
        if (
          mountedRef.current
          && isCurrentLoad
          && initialLoadIdRef.current === initialLoadId
        ) {
          setNotifications([]);
          setIsMore(false);
          setLastId(null);
          toast.error(getErrorMessage(error, notificationsCopy.loadFailed));
        }
      })
      .finally(() => {
        if (
          mountedRef.current
          && isCurrentLoad
          && initialLoadIdRef.current === initialLoadId
        ) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrentLoad = false;
    };
  }, [loadNotificationsPage, notificationsCopy.loadFailed]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    if (!notifications.length) {
      return undefined;
    }

    const unreadNotifications = notifications.filter((notification) => !notification.isRead);

    if (!unreadNotifications.length) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      unreadNotifications.forEach((notification) => markNotificationViewed(notification.id));
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const notificationId = entry.target.getAttribute('data-notification-id');

        if (!notificationId) {
          return;
        }

        markNotificationViewed(notificationId);
        observer.unobserve(entry.target);
      });
    }, {
      root: null,
      threshold: 0.65,
    });

    unreadNotifications.forEach((notification) => {
      const node = cardRefs.current.get(notification.id);

      if (node) {
        observer.observe(node);
      }
    });

    return () => observer.disconnect();
  }, [markNotificationViewed, notifications]);

  useEffect(() => {
    if (
      !isMore
        || !paginationRef.current
        || !contentRef.current
        || typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isLoadingMoreRef.current) {
        loadMoreNotifications();
      }
    }, {
      root: contentRef.current,
      rootMargin: '0px 0px 140px 0px',
      threshold: 0.1,
    });

    observer.observe(paginationRef.current);

    return () => observer.disconnect();
  }, [isMore, loadMoreNotifications]);

  return (
    <>
      <section className="notif-inbox">
        <div className="notif-inbox__content" ref={contentRef}>
        <header className="notif-inbox__header">
          <button
            type="button"
            className="notif-inbox__back"
            onClick={handleBack}
          >
            <LeftArrowIcon className="notif-inbox__back-icon" />
            <span className="notif-inbox__back-title">{notificationsCopy.title}</span>
          </button>
          {hasUnread && (
            <button
              type="button"
              className="notif-inbox__mark-all"
              disabled={isLoading || isMarkingAllRead}
              onClick={handleMarkAllRead}
            >
              {notificationsCopy.markAllRead}
            </button>
          )}
        </header>

        {isLoading ? (
          <div className="notif-inbox__state">
            <MiniPreloader label={notificationsCopy.loading} />
            <span className="notif-inbox__state-text">{notificationsCopy.loading}</span>
          </div>
        ) : groups.length ? (
          <>
            {groups.map((group) => (
              <section key={group.key} className="notif-inbox__group">
                <h2 className="notif-inbox__group-title">
                  {notificationsCopy[group.key] || NOTIFICATION_GROUP_LABELS[group.key] || group.key}
                </h2>
                <div className="notif-inbox__card">
                  {group.items.map((notification) => (
                    <div key={notification.id} className="notif-inbox__item-wrap">
                      <NotificationItem
                        language={language}
                        notification={notification}
                        onSelect={handleSelect}
                        setCardRef={setCardRef}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <div ref={paginationRef} className="notif-inbox__pagination">
              {isLoadingMore && <MiniPreloader label={notificationsCopy.loadingMore} />}
              {!isLoadingMore && isMore && (
                <button
                  type="button"
                  className="notif-inbox__load-more"
                  onClick={loadMoreNotifications}
                >
                  {notificationsCopy.loadMore}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="notif-inbox__state">
            <NotificationIcon className="notif-inbox__empty-icon" />
            <span className="notif-inbox__empty-title">{notificationsCopy.emptyTitle}</span>
            <span className="notif-inbox__state-text">{notificationsCopy.emptyText}</span>
          </div>
        )}
        </div>
      </section>

      <AnimatePresence>
        {selectedNotification && (
          <NotificationDetailSheet
            key={selectedNotification.id}
            language={language}
            notification={selectedNotification}
            notificationsCopy={notificationsCopy}
            onClose={() => setSelectedNotificationId(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default NotifInbox;
